import React, { useCallback, useRef, memo, Fragment} from 'react';

import IconButton from '@mui/material/IconButton'
import AddIcon from '@mui/icons-material/Add'
import ClearIcon from '@mui/icons-material/Clear'

import useIsMobile from './useIsMobile'


const AddButton = memo(({onClick}: {onClick: () => void}) =>
    <IconButton sx={{ padding: 0 }} onClick={onClick}>
        <AddIcon fontSize='large'/>
    </IconButton>
)

const DeleteButton = memo(({id, onClick}: {id: number, onClick: (id: number) => void}) =>
    <IconButton sx={{ padding: 0 }} onClick={() => onClick(id)}>
        <ClearIcon fontSize='large'/>
    </IconButton>
)

type Child = React.ReactNode & {props: {rowId: number}}

function ListEditor({gridTemplateColumns, rowGap, columnGap, children, getIdFromChild, onCreateRow, onDeleteRow}:{
    gridTemplateColumns: string,
    rowGap: string,
    columnGap: string,
    children: Child[],
    getIdFromChild: (child: Child) => number,
    onCreateRow: () => void,
    onDeleteRow: (rowId: number) => void
}) {
    // On a phone the multi-column row (e.g. cost-centre + project + account + amount) is far wider
    // than the viewport and can't wrap, which drags the whole page sideways. Collapse it to a
    // single column so each field stacks full-width; the delete button's extra column is dropped
    // and it flows inline. Breakpoint matches the CSS in style.css.
    const stacked = useIsMobile()
    return (
        <div style={{
            width: '100%',
            display: 'grid',
            gridTemplateColumns: stacked ? '1fr' : gridTemplateColumns + ' min-content',
            rowGap: rowGap,
            columnGap: columnGap,
            alignItems: 'end'}}>
            {children.map(child =>
                <Fragment key={getIdFromChild(child)}>
                    {child}
                    {children.length > 1 && <DeleteButton id={getIdFromChild(child)} onClick={onDeleteRow}/>}
                </Fragment>
            )}
            <div style={{ gridColumnStart: 1 }}>
                <AddButton onClick={onCreateRow}/>
            </div>
        </div>
    )
}

const getIdFromChild = (child: Child) => child.props.rowId

type RowProps<Row> = {
    rowId: number,
    rowIndex: number,
    row: Row,
    onChangeRow: (rowId: number, rowModifier: (row: Row) => void) => void
}

function ArrayEditor<Row>({gridTemplateColumns, rowGap, columnGap, rows, onChangeRows, rowConstructor, RowComponent}: {
    gridTemplateColumns: string,
    rowGap: string,
    columnGap: string,
    rows: Row[],
    onChangeRows: (rowsModifier: (rows: Row[]) => void) => void,
    rowConstructor: () => Row,
    RowComponent: React.ComponentType<RowProps<Row>>
}) {

    const rowIds = useRef([...Array(rows.length).keys()])

    const onChangeRow = useCallback(
        (rowId: number, rowModifier: (row: Row) => void) => {
            const rowIndex = rowIds.current.indexOf(rowId)
            onChangeRows(rows => { rowModifier(rows[rowIndex]) })
        },
        [onChangeRows]
    )

    const handleDeleteRow = useCallback(
        (rowId: number) => {
            const rowIndex = rowIds.current.indexOf(rowId)
            rowIds.current.splice(rowIndex, 1)
            onChangeRows(rows => { rows.splice(rowIndex, 1) })
        },
        [onChangeRows]
    )

    const handleCreateRow = useCallback(
        () => {
            const newRow = rowConstructor()
            const newRowId = rowIds.current[rowIds.current.length - 1] + 1
            rowIds.current.push(newRowId)
            onChangeRows(rows => { rows.push(newRow) })
        },
        [rowConstructor, onChangeRows]
    )

    return (
        <ListEditor
            gridTemplateColumns={gridTemplateColumns}
            rowGap={rowGap}
            columnGap={columnGap}
            getIdFromChild={getIdFromChild}
            onCreateRow={handleCreateRow}
            onDeleteRow={handleDeleteRow}
        >
            {rows.map((row, rowIndex) =>
                <RowComponent
                    key={rowIds.current[rowIndex]}
                    rowId={rowIds.current[rowIndex]}
                    rowIndex={rowIndex}
                    row={row}
                    onChangeRow={onChangeRow}
                />
            )}
        </ListEditor>
    )
}

export default ArrayEditor
